import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  TrackByFunction
} from '@angular/core'
import { notNullOrUndefined } from '../../shared/functions/type-guards'
import { combineLatest, forkJoin, iif, merge, Observable, of, ReplaySubject, Subject } from 'rxjs'
import { WatchAnalyticsService } from '../services/watch-analytics.service'
import { EventLite, Link, Row, Section } from '@flocasts/flosports30-types/dist/watch'
import {
  delay,
  distinctUntilChanged,
  filter,
  flatMap,
  map,
  scan,
  shareReplay,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom
} from 'rxjs/operators'
import { VerticalService } from '../../singleton-services/vertical.service'
import { WatchDataService } from '../services/watch-data.service'
import { AdComponentInputs } from '../../shared/ads/ad/ad-component.interface'
import { AdConfig, AdSizes, AdTargeting, AdTypes, EqAdTargeting } from '../../shared/ads/advertising-service/ad-config'
import {
  AD_ADHESION_SIZE,
  AD_INTERSTITIAL_SIZE,
  AD_LEADERBOARD_LARGE_FLEX_SIZE,
  AD_LEADERBOARD_SMALL_SIZE,
  AD_RECTANGLE_FLEX_SIZE
} from '../../shared/ads/ads.utility'
import { DisplaySection, DisplaySectionItem, StreamResponseWithLiveEventId } from '../utility/watch.interfaces'
import {
  DEFAULT_ADDITIONAL_OPTS,
  filterEventLitesByStatus,
  filterToEventLites,
  filterToLargeCards,
  filterToLiveAndUpcoming,
  mapEventLitesToActiveStreams,
  mapLargeCardsToPossibleGeoBlockedIds,
  prepFeaturedCardsForDisplay,
  prepLargeCardsForDisplay,
  prepSmallCardsForDisplay
} from '../utility/watch.utility'
import { AuthService } from '../../singleton-services/auth/auth.service'
import { GeoIPResponseItem } from 'src/app/shared/geo-ip/geo-ip.service'
import { PlatformService } from 'src/app/singleton-services/platform.service'
import { GeoBlockingService } from '../../singleton-services/geo-blocking.service'
import { DOCUMENT } from '@angular/common'
import { AdComponent } from '../../shared/ads/ad/ad.component'
import { cleanUrlFromApi } from 'src/app/data/data.utility'

@Component({
  selector: 'flo-watch',
  templateUrl: './watch.component.html',
  styleUrls: ['./watch.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WatchComponent implements OnInit, OnDestroy {
  public pageNumber = 1 // Page marker to trigger/reset infinite scroll
  private readonly ngOnDestroy$ = new Subject<void>()
  private readonly ngOnInit$ = new ReplaySubject<void>(1)
  private readonly seeMoreInView$ = new Subject<string>()
  constructor(
    private readonly watchDataService: WatchDataService,
    private readonly analytics: WatchAnalyticsService,
    private readonly verticalService: VerticalService,
    private readonly authService: AuthService,
    private readonly ps: PlatformService,
    private readonly geoBlockingService: GeoBlockingService,
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private doc: HTMLDocument
  ) {
    this.analytics.sendPageCall()
  }

  /**
   * First array of sections from the API when landing on the page.
   */
  public initialWatchSections$: Observable<ReadonlyArray<Section>> = this.verticalService.siteId$.pipe(
    filter(notNullOrUndefined),
    flatMap(siteId => this.watchDataService.getWatchSections(siteId, 0)),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )

  public initialNextPage$ = this.initialWatchSections$.pipe(map(mapLinkTypeToUrl), map(cleanUrlFromApi))

  /**
   * adding in a class of for each new page request to have a fade-in class applied for the new data
   * also adding delay(300) since sometimes the request happens so fast its almost jarring.
   * ensures a little bit of a natural feeling of loading
   */
  public callNextPage$ = this.seeMoreInView$.pipe(
    distinctUntilChanged(),
    filter(notNullOrUndefined),
    tap(() => this.analytics.sendScrollingPage(++this.pageNumber)),
    flatMap(nextPage =>
      iif(() => testWatchSectionsUrl(nextPage), this.watchDataService.getWatchSectionsByUrl(nextPage), of([]))
    ),
    map((sections: ReadonlyArray<Section>) => {
      return sections.map(section => ({ ...section, fadeIn: true }))
    }),
    delay(300),
    shareReplay(1)
  )

  public nextPage$ = this.callNextPage$.pipe(map(mapLinkTypeToUrl), map(cleanUrlFromApi))
  public currentPage$ = merge(this.initialNextPage$, this.nextPage$).pipe(shareReplay(1))

  private currentPageCall$: Observable<ReadonlyArray<Section>> = merge(
    this.initialWatchSections$,
    this.callNextPage$
  ).pipe(shareReplay(1))

  /**
   * Observable keeping track of all events coming from the API that are possibly geo-blocked,
   * and making the API call (only when SPA is loaded, NOT on SSR) with their live event id's to verify.
   */
  public geoBlockedIds$: Observable<ReadonlyArray<number>> = this.currentPageCall$.pipe(
    filter(() => this.ps.isBrowser),
    map(filterToLargeCards),
    map(mapLargeCardsToPossibleGeoBlockedIds),
    filter(ids => ids.length > 0),
    flatMap(this.geoBlockingService.getGeoBlockedLiveEvents),
    map((geoIpResponseItem: ReadonlyArray<GeoIPResponseItem>) => geoIpResponseItem.map(item => item.id)),
    scan((acc, curr) => [...acc, ...curr], []),
    shareReplay(1),
    takeUntil(this.ngOnDestroy$)
  )

  /**
   * Observable of all events with status 'LIVE.'
   */
  public filteredLiveEvents$ = this.currentPageCall$.pipe(
    map(filterToLargeCards),
    filter(notNullOrUndefined),
    map(filterToLiveAndUpcoming),
    map(liveSections => liveSections[0]),
    filter(notNullOrUndefined),
    map(liveSection => liveSection.items),
    map(filterToEventLites),
    filter(notNullOrUndefined),
    map(events => filterEventLitesByStatus(events, 'LIVE')),
    filter((liveEvents: ReadonlyArray<EventLite>) => liveEvents.length > 0),
    shareReplay(1),
    takeUntil(this.ngOnDestroy$)
  )

  /**
   * Filter all live events down to their first active stream,
   * then call the Live API with that streamId to build an array of preview tokens.
   */
  public previewStreamTokens$: Observable<ReadonlyArray<StreamResponseWithLiveEventId>> = this.filteredLiveEvents$.pipe(
    filter(() => this.ps.isBrowser),
    map(mapEventLitesToActiveStreams),
    flatMap(streamList => {
      return forkJoin([
        ...streamList.map(stream =>
          combineLatest([this.watchDataService.getLivePreviewStreamToken(stream.stream_id), of(stream.liveEventId)])
        )
      ])
    }),
    map(tokensAndLiveIds => tokensAndLiveIds.map(([tokenRes, liveEventId]) => ({ ...tokenRes, liveEventId }))),
    shareReplay(1)
  )

  /**
   * Send Preview Stream Displayed event.
   */
  public sendPreviewStreamDisplayed(liveEventId: number) {
    this.analytics.sendPreviewStreamDisplayed(liveEventId)
  }

  /**
   * Observable of all sections on display, both initial and any added in pagination (infinite scroll).
   */
  public accumulatedWatchSections$: Observable<ReadonlyArray<DisplaySection>> = this.currentPageCall$.pipe(
    scan((acc, curr) => {
      // Only append sections if it's a different page of data
      if (mapLinkTypeToUrl(acc) !== mapLinkTypeToUrl(curr)) {
        return [...acc, ...curr]
      }
      // Otherwise we've made dupe calls from server to browser
      // So remove the server sections & add the browser sections
      // We also need to reset the row count so segment events are correct
      return [...curr]
    }),
    withLatestFrom(this.authService.isPremium$),
    map(([sections, userIsPremium]) => {
      return this.prepSectionsForDisplay(sections, userIsPremium)
    }),
    shareReplay(1)
  )

  public isLoading$ = combineLatest([this.currentPage$, this.seeMoreInView$.pipe(filter(notNullOrUndefined))]).pipe(
    map(([nextPage, currentPage]) => nextPage === currentPage)
  )

  public trackBySectionId: TrackByFunction<DisplaySection> = (i, section) => section.id

  public trackByItem: TrackByFunction<DisplaySectionItem> = (i, item) => item.type

  public ngOnInit(): void {
    this.changeBodyBgColor()
    this.ngOnInit$.next()
    this.ngOnInit$.complete()
  }

  public ngOnDestroy(): void {
    this.removeBodyBgColor()
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }

  public emitNextPage(page: string) {
    this.seeMoreInView$.next(page)
  }

  private changeBodyBgColor() {
    this.renderer.addClass(this.doc.body, 'bg-grey-900')
  }

  private removeBodyBgColor() {
    this.renderer.removeClass(this.doc.body, 'bg-grey-900')
  }

  /* Ads */
  public AdComponent = import('../../shared/ads/ad/ad.component').then(m => m.AdComponent)

  public readonly adConfigMap = new Map<AdTypes, AdComponentInputs>()

  private readonly adTargeting$: Observable<AdTargeting> = this.authService.isPremium$.pipe(
    map(isPremium => ({
      isPremium
    })),
    distinctUntilChanged(EqAdTargeting.equals),
    shareReplay(1)
  )

  public readonly adConfigs$: Observable<Map<AdTypes, AdComponentInputs>> = this.adTargeting$.pipe(
    map(targeting => {
      this.adConfigMap.set('interstitial', {
        config: {
          adConfig: AdConfig(`wow/interstitial`, AD_INTERSTITIAL_SIZE, 'interstitial', targeting)
        }
      })

      return this.adConfigMap
    })
  )

  public leaderboardDesktopAdProps: AdComponentInputs = {
    config: {
      adConfig: AdConfig('wow/728x90_flex_1', AD_LEADERBOARD_LARGE_FLEX_SIZE, 'leaderboard-desktop'),
      additionalOpts: DEFAULT_ADDITIONAL_OPTS
    }
  }

  public leaderboardTabletAdProps: AdComponentInputs = {
    config: {
      adConfig: AdConfig('wow/728x90_flex_1', AD_LEADERBOARD_SMALL_SIZE, 'leaderboard-tablet'),
      additionalOpts: DEFAULT_ADDITIONAL_OPTS
    }
  }

  public leaderboardMobileAdProps: AdComponentInputs = {
    config: {
      adConfig: AdConfig('wow/300x250_flex_1', AD_ADHESION_SIZE, 'leaderboard-mobile'),
      additionalOpts: DEFAULT_ADDITIONAL_OPTS
    }
  }

  public adhesionAdProps: AdComponentInputs = {
    config: {
      adConfig: AdConfig('wow/adhesion', AD_ADHESION_SIZE, 'mobile-adhesion')
    }
  }

  /**
   * Get AdComponentInputs based on device, allowed sizes, and positioning (index) within the list.
   */
  public readonly adSizes: Record<'desktop' | 'tablet' | 'mobileLeaderboard' | 'mobileInline' | 'adhesion', AdSizes> = {
    desktop: AD_LEADERBOARD_LARGE_FLEX_SIZE,
    tablet: AD_LEADERBOARD_SMALL_SIZE,
    mobileLeaderboard: AD_ADHESION_SIZE,
    mobileInline: AD_RECTANGLE_FLEX_SIZE,
    adhesion: AD_ADHESION_SIZE
  }
  public getInlineAdProps(sizes: AdSizes, index: number, isMobile = false): AdComponentInputs {
    const adPosition = (index - 1) / 3
    const adDirectory = isMobile ? 'wow/300x250_flex_btf' : 'wow/728x90_flex_btf'

    return {
      config: {
        adConfig: AdConfig(adDirectory, sizes, `leaderboard-inline-${adPosition}`, { adPosition })
      }
    }
  }

  /**
   * Map each Section coming from the API to the appropriate DisplaySection.
   */
  private prepSectionsForDisplay = (
    sections: ReadonlyArray<Section>,
    userIsPremium: boolean
  ): ReadonlyArray<DisplaySection> => {
    return sections.map((section: Section, sectionIndex: number) => {
      const sectionRowNumber = sectionIndex
      const arrayLength = section.items?.length
      switch (section.items_style) {
        case 'shortcut':
          return section
        case 'featured-card':
          const featuredCardName = section.title
          return {
            ...section,
            items: prepFeaturedCardsForDisplay(section, sectionRowNumber, arrayLength, featuredCardName, userIsPremium)
          }
        case 'large-card':
          const largeCardName = section.title
          return {
            ...section,
            items: prepLargeCardsForDisplay(section, sectionRowNumber, arrayLength, largeCardName, userIsPremium)
          }
        case 'small-card':
          const smallCardName = section.title
          return {
            ...section,
            items: prepSmallCardsForDisplay(section, sectionRowNumber, arrayLength, smallCardName, userIsPremium)
          }
        default:
          return section
      }
    })
  }
}

function mapLinkTypeToUrl(sections: ReadonlyArray<Row>) {
  return sections.filter((section: Row): section is Link => section.type === 'link').map(section => section.url)[0]
}

function testWatchSectionsUrl(url: string): boolean {
  return /^\experiences\//.test(url)
}
